import { Directive, HostBinding, HostListener, Input, inject } from '@angular/core';
import { Router } from '@angular/router';
import { executeNavigationFromConfig, resolveNavigationFromConfig } from '../../shared-services/chart-wrapper.service';
import { readRowField, type TableCellConfig } from './table-cell-config';
import { navigationFilterQueryParams, type NavigationFilterEntry } from '../../shared-services/navigation-filters.utils';

// ── Pure helpers — shared by the anchor directive below AND the plain cell
//    click paths of every table renderer (Table & Matrix, Nested, Drill-Down),
//    so a column navigates the same way whatever its display kind is. ─────────

/** Whether a column's cell configuration carries any click navigation
 *  (published dashboard with row filter, or a button-style route action). */
export function hasTableCellNavigation(config: TableCellConfig | null | undefined): boolean {
  return !!(config?.navigation || config?.navigateAction);
}

/** Where a column's navigation goes, for titles / hints ('' when none). */
export function tableCellNavigationTarget(config: TableCellConfig | null | undefined): string {
  if (config?.navigation?.targetDashboard) return String(config.navigation.targetDashboard);
  const action = config?.navigateAction;
  return action ? String(action['route'] || action['path'] || action['url'] || '') : '';
}

/** Resolve the button-compatible navigate action for ONE clicked cell:
 *  a dashboard navigation becomes `/dvm-published/<slug>` with the row's
 *  filter value as query params; a route action is passed through. Null when
 *  nothing is configured or the row has no value for the filter field.
 *
 *  The filter field is the configured one, else `defaultFilterKey` (a
 *  renderer passes its table's FIRST column for a NUMBER column — a cost
 *  value is never what a target dashboard filters on, the row's key is), else
 *  the clicked column itself. */
export function tableCellNavigationAction(
  config: TableCellConfig | null | undefined,
  row: Record<string, unknown> | null | undefined,
  field: string,
  value: unknown,
  defaultFilterKey?: string,
): Record<string, unknown> | null {
  if (!config) return null;
  const rec = row ?? {};
  let action = config.navigateAction;
  if (config.navigation) {
    const nav = config.navigation;
    const key = nav.filterKey || defaultFilterKey || field;
    const rowValue = (name: string): unknown => {
      const own = Object.prototype.hasOwnProperty.call(rec, name) ? rec[name] : readRowField(rec, name);
      return name === field ? (own ?? value) : own;
    };
    const resolved = rowValue(key);
    if (!nav.targetDashboard || !key || resolved == null || resolved === '') return null;
    // The main filter first, then every extra column of the same row that has a value.
    const entries: NavigationFilterEntry[] = [{ key, value: String(resolved) }];
    (nav.filterKeys ?? []).forEach((extra) => {
      const extraValue = rowValue(extra);
      if (extra && extra !== key && extraValue != null && extraValue !== '') entries.push({ key: extra, value: String(extraValue) });
    });
    action = {
      route: `/dvm-published/${nav.targetDashboard}`,
      queryParams: { ...navigationFilterQueryParams(entries), filterTargets: (nav.targetComponents ?? []).join(',') },
      openInNewTab: nav.openInNewTab,
    };
  }
  return action && (action['route'] || action['path'] || action['url']) ? action : null;
}

/** Perform a cell's navigation (ctrl / meta / shift-click → new tab). Returns
 *  false when nothing is configured for this row, so callers can fall through
 *  to their ordinary cell behaviour. */
export function runTableCellNavigation(
  router: Router | null | undefined,
  config: TableCellConfig | null | undefined,
  row: Record<string, unknown> | null | undefined,
  field: string,
  value: unknown,
  event?: MouseEvent | Event | null,
  defaultFilterKey?: string,
): boolean {
  let action = tableCellNavigationAction(config, row, field, value, defaultFilterKey);
  if (!action || !router) return false;
  const mouse = event as MouseEvent | null | undefined;
  if (mouse && (mouse.ctrlKey || mouse.metaKey || mouse.shiftKey)) action = { ...action, openInNewTab: true };
  const params = { name: field, value, data: { raw: row ?? {} } };
  return executeNavigationFromConfig(router, { navigateAction: action }, params, { row: row ?? {}, field });
}

/** True when the click landed on an interactive child (editor, button, link)
 *  — a cell-level navigation must not hijack those. */
export function isInteractiveCellTarget(event: Event | null | undefined): boolean {
  const target = event?.target as HTMLElement | null | undefined;
  return !!target?.closest?.('a, button, input, select, textarea, [role="button"]');
}

/** Shared by every table renderer; ordinary URL links retain native behavior. */
@Directive({ selector: 'a[tableCellNavigation]', standalone: true })
export class TableCellNavigationDirective {
  @Input() tableCellNavigation: TableCellConfig | null | undefined;
  @Input() navigationRow: Record<string, unknown> | null | undefined;
  @Input() navigationField = '';
  @Input() navigationValue: unknown;
  @Input() navigationHref = '#';
  /** Filter field when none is configured (a number column's table key). */
  @Input() navigationDefaultKey = '';
  private readonly router = inject(Router);

  @HostBinding('attr.href') get href(): string | null {
    if (!this.configured) return this.navigationHref;
    const action = this.action();
    const resolved = action ? resolveNavigationFromConfig({ navigateAction: action }, this.params(), this.context()) : null;
    return resolved ? this.router.serializeUrl(this.router.createUrlTree([resolved.path], { queryParams: resolved.queryParams })) : null;
  }
  @HostBinding('attr.target') get target(): string | null {
    if (!this.configured) return '_blank';
    const action = this.action();
    return action?.['openInNewTab'] || action?.['openIn'] === 'new_tab' ? '_blank' : null;
  }
  @HostBinding('attr.aria-disabled') get disabled(): string | null { return this.configured && !this.action() ? 'true' : null; }
  private get configured(): boolean { return hasTableCellNavigation(this.tableCellNavigation); }
  private params() { return { name: this.navigationField, value: this.navigationValue, data: { raw: this.navigationRow ?? {} } }; }
  private context() { return { row: this.navigationRow ?? {}, field: this.navigationField }; }

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent): void {
    if (!this.configured) return;
    event.preventDefault();
    event.stopPropagation();
    runTableCellNavigation(this.router, this.tableCellNavigation, this.navigationRow, this.navigationField, this.navigationValue, event, this.navigationDefaultKey);
  }

  private action(): Record<string, unknown> | null {
    return tableCellNavigationAction(this.tableCellNavigation, this.navigationRow, this.navigationField, this.navigationValue, this.navigationDefaultKey);
  }
}
